import { createDraftOrder } from "@repo/shopify/api/createDraftOrder";
import useCartInfo from "@repo/shopify/hooks/useCartInfo";
import useCustomerInfo from "@repo/shopify/hooks/useCustomerInfo";
import { getStoredGoogleCampaignId, getStoredGoogleTrafficSource } from "@repo/shopify/hooks/useGoogleTrafficSource";
import { getStoredMetaCampaignId, getStoredMetaTrafficSource } from "@repo/shopify/hooks/useMetaTrafficSource";
import { getStoredNaverCampaignId, getStoredNaverTrafficSource } from "@repo/shopify/hooks/useNaverTrafficSource";
import { isValidAddress } from "@repo/shopify/utils/address";
import { checkQuantityLimit } from "@repo/shopify/utils/cart";
import { extractId } from "@repo/shopify/utils/common";
import { formatPhone } from "@repo/shopify/utils/format";
import type { TossPaymentsWidgets, WidgetPaymentMethodWidget } from "@tosspayments/tosspayments-sdk";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { t } from "i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import Button from "@atoms/Button";
import Input from "@atoms/Input";
import Loading from "@atoms/Loading";
import Price from "@atoms/Price";

import CheckoutLineItem from "@molecules/CheckoutLineItem";

import ShippingAddress from "@organisms/Address/ShippingAddress";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useCartQuery from "@hooks/useCartQuery";
import useCheckPurchaseLimit from "@hooks/useCheckPurchaseLimit";
import useCustomerQuery from "@hooks/useCustomerQuery";
import useFormErrorHandler from "@hooks/useFormErrorHandler";

import { useCartStore } from "@store/cart";
import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

import { formatAutoDiscountTitle } from "@utils/discount";

interface IFormData {
  discountCode: string;
}

const CLIENT_KEY = import.meta.env.VITE_REACT_APP_TOSS_CLIENT_KEY as string;
const TINT_PAY_PARAM = "TINT_PAY_PARAM";

const BLOCKED_ADDRESS = [
  { zip: "05842", roadname_address: "송파구 송파대로 55", lotnumber_address: "서울 송파구 장지동 875" }, // 크림
  { zip: "07983", roadname_address: "양천구 목동동로 363", lotnumber_address: "서울 양천구 목동 906" }, // 솔드아웃
  { zip: "22769", roadname_address: "서구 원석로 196", lotnumber_address: "인천 서구 원창동 488" }, // 로지스허브
];

const modalTitles: Record<string, string> = {
  paymentError: "Payment Error",
  invalidCart: "Checkout Error",
  checkoutError: "Checkout Error",
  overQuantity: "Quantity Limit",
};

export default function CheckoutKr() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 토스 임직원 구매 한도 체크
  const { remainingLimit, expectedRemainingLimit } = useCheckPurchaseLimit();

  const { cart } = useCartStore();
  const { customer, token, logout } = useCustomerStore();
  const { openToast, openModal, openAccount } = useLayoutStore();

  const { fetchCustomer } = useCustomerQuery();
  const { updateCartLine, discountCart, discountCartLoading } = useCartQuery();

  const { isEmployee, isPartnership, membership, isMembershipDiscountCodeUsed, addresses, codeUsableMembership } =
    useCustomerInfo(customer, "thisisneverthat");

  const {
    totalAmount,
    shippingAmount,
    originTotalAmount,
    autoDiscountAmount,
    codeDiscountAmount,
    codeDiscountTitle,
    autoDiscountTitle,
    orderName,
  } = useCartInfo(cart);

  const [isInitLoading, setIsInitLoading] = useState(true); // 초기 로딩
  const [isLoading, setIsLoading] = useState<boolean>(false); // 주문 생성 로딩
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false); // 결제 요청 로딩
  const [isAddressMutateLoading, setIsAddressMutateLoading] = useState<boolean>(false); // 배송지 로딩
  const [selectedAddressId, setSelectedAddressId] = useState<string>(customer?.defaultAddress?.id || ""); // 선택된 주소 ID
  const [isDiscountCodeApplied, setIsDiscountCodeApplied] = useState(false); // 할인 코드 적용 여부

  const tossPaymentsRef = useRef<TossPaymentsWidgets | null>(null);
  const tossPaymentsWidgetRef = useRef<WidgetPaymentMethodWidget | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    getValues,
    formState: { errors },
  } = useForm<IFormData>({ defaultValues: { discountCode: "" }, mode: "onChange" });

  const { discountCode } = watch();

  const { onCommonError } = useFormErrorHandler({ values: getValues(), errors });

  useEffect(() => {
    void init();

    return () => {
      void applyDiscountCode("");
    };
  }, []);

  const init = async () => {
    try {
      const isSearchParamsHandled = await handleSearchParams();
      if (isSearchParamsHandled) return;

      const isCustomerValid = await checkCustomer();
      if (!isCustomerValid) return;

      await applyDiscountCode("");

      await initializeTossWidget();

      const { data } = await updateCartLine({
        variables: { cartId: cart.id, lines: cart.lines.edges.map(({ node }) => ({ id: node.id })) },
      });

      if (data?.cartLinesUpdate.cart?.lines?.edges.some(({ node }) => node.quantity <= 0)) {
        await updateCartLine({
          variables: {
            cartId: cart.id,
            lines: data?.cartLinesUpdate.cart.lines.edges.map(({ node }) => ({ id: node.id })),
          },
        });
      }
    } catch (error) {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      navigate(URLS.HOME);
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    } finally {
      setIsInitLoading(false);
    }
  };

  const handleSearchParams = async () => {
    const orderId = searchParams.get("orderId");
    const paymentKey = searchParams.get("paymentKey");
    const amount = searchParams.get("amount");
    const errorMessage = searchParams.get("message");

    console.log("[THISISNEVERTHAT KR] SEARCH PARAMS", { orderId, paymentKey, amount, errorMessage });

    if (errorMessage) {
      setTimeout(() => {
        openValidationModal("paymentError", errorMessage);
      }, 300);
      return false;
    }

    if (paymentKey && orderId && amount) {
      await completePaymentRequest({ paymentKey, amount, orderId });
      return true;
    }

    return false;
  };

  const checkCustomer = async () => {
    // 로그인 하지 않은 경우
    if (!token.accessToken) {
      openToast(NOTIFICATIONS.WARNING_NO_LOGIN);
      navigate(URLS.HOME);
      setTimeout(() => {
        openAccount(true);
      }, 300);
      return false;
    }

    // 유효하지 않은 토큰 (소셜로그인 동시접속 등으로 인해 토큰 만료)
    const { data } = await fetchCustomer({ variables: { customerAccessToken: token.accessToken } });
    if (!data?.customer) {
      openToast(NOTIFICATIONS.WARNING_END_SESSION);
      navigate(URLS.HOME);
      logout();
      setTimeout(() => {
        openAccount(true);
      }, 300);
      return false;
    }

    return true;
  };

  const initializeTossWidget = async () => {
    if (tossPaymentsWidgetRef.current) await tossPaymentsWidgetRef.current.destroy();

    const tossPayments = await loadTossPayments(CLIENT_KEY);

    tossPaymentsRef.current = tossPayments.widgets({ customerKey: extractId(customer.id) });
    await tossPaymentsRef.current.setAmount({ value: 99999, currency: "KRW" });

    tossPaymentsWidgetRef.current = await tossPaymentsRef.current.renderPaymentMethods({
      selector: "#payment-method",
      variantKey: "TNT_KR_default",
    });
  };

  // 카트 검증
  useEffect(() => {
    if (isLoading) return;

    if (!cart.lines.edges.length) {
      openToast(NOTIFICATIONS.WARNING_NO_CART);
      navigate(URLS.HOME);
    }

    // 토스 파트너십 태그가 있는 고객의 경우, 구매 한도 초과시 홈으로 이동
    if (isPartnership) {
      if (remainingLimit <= 0 || expectedRemainingLimit < 0) {
        openToast(NOTIFICATIONS.WARNING_EXCEEDED_PURCHASE_LIMIT);
        navigate(URLS.HOME);
      }
    }
  }, [cart, isLoading, isPartnership, remainingLimit, expectedRemainingLimit]);

  // 선택된 주소
  const selectedAddress = useMemo(
    () => addresses?.find((address) => extractId(address.id) === extractId(selectedAddressId)),
    [addresses, selectedAddressId],
  );

  // 주문생성 API 호출 Parameter 데이터
  const getLambdaPaymentParam = () => {
    if (!cart.lines.edges.length) return null;

    const orderDiscountCode = cart.discountCodes.find((item) => item.applicable)?.code || "";

    const address = {
      firstName: selectedAddress?.firstName || "",
      lastName: selectedAddress?.lastName || "",
      phone: formatPhone(selectedAddress?.phone || ""),
      zip: selectedAddress?.zip || "",
      address1: selectedAddress?.address1 || "",
      address2: selectedAddress?.address2 || "",
      country: selectedAddress?.country || "South Korea",
      province: selectedAddress?.province || "",
      city: selectedAddress?.city || "-",
      company: "",
    };

    const googleTrafficSource = getStoredGoogleTrafficSource();
    const googleCampaignId = getStoredGoogleCampaignId();
    const naverTrafficSource = getStoredNaverTrafficSource();
    const naverCampaignId = getStoredNaverCampaignId();
    const metaTrafficSource = getStoredMetaTrafficSource();
    const metaCampaignId = getStoredMetaCampaignId();

    return {
      orderData: {
        purchasingEntity: { customerId: customer.id },
        shippingLine: { shippingRateHandle: "CJ 대한통운" },
        lineItems: cart.lines.edges.map(({ node }) => ({
          variantId: node.merchandise.id,
          quantity: node.quantity,
        })),
        shippingAddress: address,
        billingAddress: address,
        customAttributes: orderDiscountCode ? [{ key: "DiscountCode", value: orderDiscountCode }] : [],
        acceptAutomaticDiscounts: true,
        discountCodes: orderDiscountCode ? [orderDiscountCode] : [],
        tags: [
          ...(isEmployee && !isPartnership ? ["Employee"] : []),
          ...(membership === orderDiscountCode.toUpperCase() ? ["멤버십_할인코드"] : []),
          ...(googleTrafficSource === "google_ads" ? ["유입_GoogleAds"] : []),
          ...(googleTrafficSource === "google_shopping" ? ["유입_GoogleShopping"] : []),
          ...(googleCampaignId ? [`유입_GoogleAds_${googleCampaignId}`.slice(0, 40)] : []),
          ...(naverTrafficSource === "naver_shopping" ? ["유입_NaverShopping"] : []),
          ...(naverTrafficSource === "naver_paid_shopping" ? ["유입_NaverPaidShopping"] : []),
          ...(naverCampaignId ? [`유입_NaverPaidShopping_${naverCampaignId}`.slice(0, 40)] : []),
          ...(metaTrafficSource === "meta_ads" ? ["유입_MetaAds"] : []),
          ...(metaCampaignId ? [`유입_MetaAds_${metaCampaignId}`.slice(0, 40)] : []),
        ],
      },
    };
  };

  const onSubmit: SubmitHandler<IFormData> = async (formData) => {
    console.log("[THISISNEVERTHAT] KR DISCOUNT CODE SUBMIT", {
      isDiscountCodeApplied,
      discountCodeValue: formData.discountCode,
    });
    if (isDiscountCodeApplied) {
      await applyDiscountCode("");
    } else {
      const discountCodeValue = formData.discountCode.trim();
      await addDiscountCode(discountCodeValue);
    }
  };

  // 토스 결제 위젯 요청
  const requestPaymentWidget = async () => {
    if (!tossPaymentsRef.current || !tossPaymentsWidgetRef.current) {
      setIsPaymentLoading(false);
      return;
    }

    const lambdaPaymentParam = getLambdaPaymentParam();
    console.log("[TINT KR] REQUEST PARAM", lambdaPaymentParam);
    if (!lambdaPaymentParam) {
      setIsPaymentLoading(false);
      return;
    }

    sessionStorage.setItem(TINT_PAY_PARAM, JSON.stringify(lambdaPaymentParam));

    try {
      await tossPaymentsRef.current.setAmount({ value: totalAmount, currency: "KRW" });
      await tossPaymentsRef.current?.requestPayment({
        orderId: `${extractId(customer.id)}_${Number(new Date())}`,
        orderName,
        customerName: customer.email || "",
        customerEmail: customer.email || "",
        successUrl: window.location.href,
        failUrl: window.location.href,
      });
    } catch (error: any) {
      openModal({
        title: "Payment Canceled",
        content: <p>{t("warning.payment_cancel")}</p>,
        confirm: {
          label: "Okay",
          onClick: () => {
            openModal(null);
          },
        },
      });

      sessionStorage.removeItem(TINT_PAY_PARAM);
      openToast(NOTIFICATIONS.ERROR_SERVER);
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`[TOSS] Failed to Fetch : ${message}`);
    } finally {
      setIsPaymentLoading(false);
    }
  };

  // 토스 결제 승인 후 주문 생성
  const completePaymentRequest = async ({
    paymentKey,
    orderId,
    amount,
  }: {
    paymentKey: string;
    orderId: string;
    amount: string;
  }) => {
    setIsLoading(true);

    // 토스 결제 요청했을 때 sessionStorage 저장된 paymentParameter
    const paymentParam = sessionStorage.getItem(TINT_PAY_PARAM);
    const paymentParameter = paymentParam ? JSON.parse(paymentParam) : null;

    console.log("[TINT KR] ORDER PAYMENT PARAM", paymentParameter);

    if (!paymentParameter) {
      setIsLoading(false);
      await initializeTossWidget();
      setSearchParams({});
      openValidationModal("paymentError", t("warning.necessary_payment_data"));
      sessionStorage.removeItem(TINT_PAY_PARAM);
      return;
    }

    try {
      const response = await createDraftOrder({
        shop: "thisisneverthat",
        param: { paymentKey, orderId, amount, ...paymentParameter },
        domain: "ko",
      });

      console.log("[THISISNEVERTHAT KR] ORDER LAMBDA RESPONSE", response);

      if (response?.status === 200) {
        navigate(`/order-completed/${response.data.orderId}`);
        sessionStorage.removeItem(TINT_PAY_PARAM);
      } else {
        setIsLoading(false);
        await initializeTossWidget();
        setSearchParams({});
        openValidationModal("paymentError", response?.data?.message || t("error.payment"));
        sessionStorage.removeItem(TINT_PAY_PARAM);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setIsLoading(false);
      await initializeTossWidget();
      setSearchParams({});
      openValidationModal("paymentError", t("error.payment"));
      sessionStorage.removeItem(TINT_PAY_PARAM);
    }
  };

  /* 유효성 검사 */
  const checkBeforePayment = async () => {
    setIsPaymentLoading(true);

    // 0. Get updated customer & Cart
    const { data: updatedCustomer } = await fetchCustomer({ variables: { customerAccessToken: token.accessToken } });
    console.log("[THISISNEVERTHAT KR] CUSTOMER: ", updatedCustomer?.customer);
    if (!updatedCustomer?.customer?.id) {
      setIsPaymentLoading(false);
      openToast(NOTIFICATIONS.WARNING_NO_LOGIN);
      navigate(URLS.HOME);
      return;
    }

    const { data: updatedCart } = await updateCartLine({
      variables: { cartId: cart.id, lines: cart.lines.edges.map(({ node }) => ({ id: node.id })) },
    });
    console.log("[THISISNEVERTHAT KR] CART: ", updatedCart?.cartLinesUpdate.cart);
    if (!updatedCart?.cartLinesUpdate.cart?.lines.edges.length) {
      setIsPaymentLoading(false);
      openToast(NOTIFICATIONS.WARNING_NO_CART);
      navigate(URLS.HOME);
      return;
    }

    // 1. Check buyerIdentity
    if (
      extractId(updatedCustomer.customer.id) !==
      extractId(updatedCart?.cartLinesUpdate.cart.buyerIdentity.customer?.id || "")
    ) {
      setIsPaymentLoading(false);
      openValidationModal("checkoutError", t("checkout.wrong_customer_id"));
      return;
    }

    // 2. Check address
    if (!selectedAddress) {
      setIsPaymentLoading(false);
      openValidationModal("checkoutError", t("warning.necessary_shipping_address"));
      return;
    }

    // 3. Check isValid address
    if (!isValidAddress(selectedAddress)) {
      setIsPaymentLoading(false);
      openValidationModal("checkoutError", t("warning.necessary_invalid_shipping_address"));
      return;
    }

    const isBlocked = BLOCKED_ADDRESS.some((blockedAddress) => {
      const selectedAddress1 = selectedAddress.address1 ?? "";
      return (
        blockedAddress.zip === selectedAddress.zip &&
        (blockedAddress.roadname_address.includes(selectedAddress1) ||
          blockedAddress.lotnumber_address.includes(selectedAddress1))
      );
    });

    if (isBlocked) {
      setIsPaymentLoading(false);
      openValidationModal("checkoutError", t("warning.checkout_shipping_address_blocked"));
      return;
    }

    const { exceeded, adjustedCartLines } = checkQuantityLimit({
      cart: updatedCart.cartLinesUpdate.cart,
      customer: updatedCustomer.customer,
    });

    if (exceeded) {
      setIsPaymentLoading(false);
      openValidationModal("overQuantity", t("warning.quantity_insufficient"), adjustedCartLines);
      return;
    }

    if (!adjustedCartLines.length) {
      setIsPaymentLoading(false);
      openValidationModal("invalidCart", t("warning.invalid_cart"));
      return;
    }

    // 9. Check discount applicable
    const discountCodes = updatedCart?.cartLinesUpdate.cart.discountCodes || [];
    if (discountCodes.length) {
      const { code, applicable } = discountCodes[0];
      if (code && applicable) {
        const isAppliedCode = await addDiscountCode(code);
        if (!isAppliedCode) {
          setIsPaymentLoading(false);
          openValidationModal("checkoutError", t("checkout.wrong_discount_code"));
          return;
        }
      }
    }

    if (totalAmount !== parseFloat(updatedCart?.cartLinesUpdate?.cart.cost.totalAmount.amount || "0")) {
      setIsPaymentLoading(false);
      openValidationModal("checkoutError", t("checkout.wrong_cart_changed"));
      return;
    }

    await requestPaymentWidget();
  };

  // 유효성 검사 실패시 케이스 별 모달 열기
  const openValidationModal = (type: string, content: string, cartLines?: { id: string; quantity: number }[]) => {
    const title = modalTitles[type];

    const callback = async () => {
      switch (type) {
        case "paymentError":
          setSearchParams({});
          break;
        case "overQuantity":
          if (cartLines) await updateCartLine({ variables: { cartId: cart.id, lines: cartLines } });
          break;
        case "invalidCart":
          await updateCartLine({ variables: { cartId: cart.id, lines: [] } });
          navigate(URLS.HOME);
          break;
        default:
          break;
      }
      openModal(null);
    };

    openModal({
      title,
      content: <p>{content}</p>,
      confirm: {
        label: "Okay",
        onClick: () => {
          void callback();
        },
      },
      onClickClose: () => {
        void callback();
      },
    });
  };

  const addDiscountCode = async (appliedCode: string) => {
    // 멤버십 할인코드 사용 했을 경우
    if (appliedCode.toUpperCase() === membership && isMembershipDiscountCodeUsed) {
      setError("discountCode", { type: "manual" });
      setValue("discountCode", "");
      openToast(NOTIFICATIONS.ERROR_DISCOUNT_CODE);
      setIsDiscountCodeApplied(false);
      return false;
    }

    const result = await applyDiscountCode(appliedCode);
    return result;
  };

  const applyDiscountCode = async (appliedCode: string) => {
    const { data } = await discountCart({
      variables: { cartId: cart.id, discountCodes: appliedCode ? [appliedCode] : [] },
    });

    // 할인코드 적용 제거
    if (appliedCode === "") {
      setValue("discountCode", "");
      setIsDiscountCodeApplied(false);
      return false;
    }

    const userErrors = data?.cartDiscountCodesUpdate.userErrors.length;
    const notApplicable = !data?.cartDiscountCodesUpdate?.cart?.discountCodes?.[0]?.applicable;

    if (userErrors || notApplicable) {
      await discountCart({ variables: { cartId: cart.id, discountCodes: [] } });
      setError("discountCode", { type: "manual" });
      setValue("discountCode", "");
      openToast(NOTIFICATIONS.ERROR_DISCOUNT_CODE);
      setIsDiscountCodeApplied(false);
      return false;
    }

    setValue("discountCode", appliedCode);
    setIsDiscountCodeApplied(true);
    return true;
  };

  return (
    <>
      {isLoading ? (
        <Loading type="page" />
      ) : (
        <>
          <h1 className="text-title">Complete Your Order</h1>
          <section className="flex flex-col gap-10">
            {/* ADDRESS */}
            <ShippingAddress
              addressList={addresses}
              isTossPartnership={isPartnership}
              selectedAddress={selectedAddress}
              selectedAddressId={selectedAddressId}
              setIsAddressMutateLoading={setIsAddressMutateLoading}
              setSelectedAddressId={setSelectedAddressId}
            />

            {/* PAYMENT METHOD */}
            <div className="flex flex-col gap-2.5">
              <h2>Payment Method</h2>
              <div id="payment-method" />
            </div>

            {/* ORDER SUMMARY */}
            <div className="flex flex-col gap-2.5">
              <h2>Order Summary</h2>
              <div className="tnt-gap-x grid grid-cols-8 gap-y-2.5">
                {/* Product */}
                {cart.lines.edges
                  ?.filter(({ node }) => !node.merchandise.product.tags.includes("Gift") && node.quantity > 0)
                  .map(({ node }) => (
                    <CheckoutLineItem key={node.id} node={node} />
                  ))}

                {/* Gift */}
                {cart.lines.edges
                  ?.filter(({ node }) => node.merchandise.product.tags.includes("Gift") && node.quantity > 0)
                  .map(({ node }) => (
                    <CheckoutLineItem key={node.id} node={node} />
                  ))}
              </div>
            </div>

            {/* DISCOUNT CODE */}
            {!isEmployee && (
              <div className="flex flex-col gap-1">
                <h2>Discount Code</h2>
                {codeUsableMembership && !isMembershipDiscountCodeUsed ? (
                  <h3 className="text-blue">{`*Monthly Discount Code: ${membership}`}</h3>
                ) : null}
                <form className="flex flex-col items-end gap-2.5" onSubmit={handleSubmit(onSubmit, onCommonError)}>
                  <Input
                    {...register("discountCode", { pattern: /^[a-zA-Z0-9-_]+$/ })}
                    {...(errors.discountCode && { isError: true })}
                    className="w-full"
                    disabled={isInitLoading || discountCartLoading || isDiscountCodeApplied}
                    placeholder="Discount Code"
                  >
                    {discountCode && !discountCartLoading ? (
                      <Button
                        as="text"
                        className="absolute top-1/2 right-0 -translate-y-1/2"
                        onClick={() => {
                          if (isDiscountCodeApplied) {
                            void applyDiscountCode("");
                          } else {
                            reset();
                          }
                        }}
                      >
                        {isDiscountCodeApplied ? "Remove" : "×"}
                      </Button>
                    ) : null}
                  </Input>
                  {isDiscountCodeApplied ? (
                    <p className="text-xs font-normal">{t("checkout.discount_code_alert_description")}</p>
                  ) : null}
                  <Button
                    as="square"
                    className="h-12! w-full"
                    disabled={isInitLoading || isDiscountCodeApplied || !discountCode || Boolean(errors.discountCode)}
                    loading={discountCartLoading}
                    type="submit"
                  >
                    {isDiscountCodeApplied ? "Submitted" : "Submit"}
                  </Button>
                </form>
              </div>
            )}

            {/* SUMMARY*/}
            <div className="pc:pb-5 flex flex-col justify-end gap-2.5 pb-4">
              <div className="flex flex-col gap-1">
                <div className="tnt-gap-x grid grid-cols-2">
                  <span>Subtotal</span>
                  <Price className="justify-end" price={originTotalAmount} />
                </div>
                <div className="tnt-gap-x grid grid-cols-2">
                  <span>Shipping</span>
                  <Price className="justify-end" price={shippingAmount} />
                </div>
                {autoDiscountAmount + codeDiscountAmount > 0 && (
                  <div className="flex flex-col">
                    <div className="tnt-gap-x grid grid-cols-2">
                      <span>Discount</span>
                      <div className="flex justify-end">
                        -<Price className="text-right" price={autoDiscountAmount + codeDiscountAmount} />
                      </div>
                    </div>

                    {autoDiscountAmount > 0 && (
                      <div className="tnt-gap-x grid grid-cols-[1fr_auto] items-baseline">
                        <span className="text-xs font-normal">({formatAutoDiscountTitle(autoDiscountTitle)})</span>
                        <div className="flex justify-end text-xs font-normal">
                          -<Price className="text-right" price={autoDiscountAmount} />
                        </div>
                      </div>
                    )}

                    {codeDiscountAmount > 0 && (
                      <div className="tnt-gap-x grid grid-cols-[1fr_auto] items-baseline">
                        <span className="text-xs font-normal">({codeDiscountTitle})</span>
                        <div className="flex justify-end text-xs font-normal">
                          -<Price className="text-right" price={codeDiscountAmount} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-x-5">
                  <span>Total</span>
                  <Price className="justify-end" price={totalAmount} />
                </div>
              </div>
              <Button
                as="square"
                className="h-12! w-full justify-center"
                disabled={isInitLoading || discountCartLoading || isPaymentLoading || isAddressMutateLoading}
                loading={isInitLoading || discountCartLoading || isPaymentLoading || isAddressMutateLoading}
                onClick={() => {
                  void checkBeforePayment();
                }}
              >
                {t("checkout.pay_now")}
              </Button>
            </div>
          </section>
        </>
      )}
    </>
  );
}
